/**
 * buildPickerSections — FE-10.1.6 picker hierarchy helper.
 *
 * Pure function that takes:
 *   - `allItems`        the user's full inventory cache (paginated)
 *   - `scenes`          the user's scenesCache (paginated)
 *   - `alreadyBoundIds` set of item ids already in this crew
 *   - `mode`            'scenes' | 'recent' | 'category'
 *
 * and produces a list of `PickerSection` rows the AddItemsToGroupSheet
 * can render with FlatList.
 *
 * Design choices baked into the locked plan:
 *   - Already-bound items are filtered out at the section level.
 *   - Scenes whose items are all already-bound are dropped (we never
 *     render a zero-eligible-item scene).
 *   - Categories with zero eligible items are dropped.
 *   - Scenes are sorted by `created_at` desc (most recent first).
 *   - Categories are sorted alphabetically.
 *   - 'recent' is a degenerate single-section dump — kept here so the
 *     consumer can use one render path regardless of mode.
 *
 * The helper is dependency-free + deterministic so it unit-tests
 * cleanly without React or Redux.
 */

import type {
  InventoryItemResponse,
  FileWithInventoryResponse,
} from '../../../../api/types';

export type PickerMode = 'scenes' | 'recent' | 'category';

export interface PickerSection {
  /** Stable key for FlatList. Unique per mode. */
  key: string;
  /** What kind of grouping this section represents. */
  mode: PickerMode;
  /** Display title shown on the section header. */
  title: string;
  /** Optional secondary line under the title. */
  subtitle?: string;
  /** Hero image (Scenes mode only — taken from FileWithInventoryResponse.storage_url). */
  heroPhotoUrl?: string;
  /** When the section corresponds to a scene, the source file id —
   *  useful for navigation back to SceneViewerScreen if we ever want
   *  to drill out of the picker. */
  fileId?: number;
  /** ISO timestamp — used by Scenes ordering + the "Last 7 days" chip. */
  createdAt?: string;
  /** Items in this section that are still ELIGIBLE for selection
   *  (already-bound items pre-filtered out). */
  items: InventoryItemResponse[];
}

/**
 * Format an ISO date as a short label ("Apr 28").
 */
function formatShortDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return null;
  }
}

/**
 * Strip filename extension. "IMG_9692.JPG" → "IMG_9692".
 */
function stripExtension(name: string | null | undefined): string {
  if (!name) return 'Untitled snapshot';
  const dot = name.lastIndexOf('.');
  if (dot <= 0) return name;
  return name.slice(0, dot);
}

/* ──────────────────────────────────────────────────────────────────────
 * Mode entry
 * ────────────────────────────────────────────────────────────────────── */

export interface BuildPickerSectionsArgs {
  mode: PickerMode;
  allItems: InventoryItemResponse[];
  scenes: FileWithInventoryResponse[];
  alreadyBoundIds: Set<number>;
}

export function buildPickerSections({
  mode,
  allItems,
  scenes,
  alreadyBoundIds,
}: BuildPickerSectionsArgs): PickerSection[] {
  switch (mode) {
    case 'recent':
      return buildRecentSection(allItems, alreadyBoundIds);
    case 'scenes':
      return buildSceneSections(scenes, alreadyBoundIds);
    case 'category':
      return buildCategorySections(allItems, alreadyBoundIds);
  }
}

/* ──────────────────────────────────────────────────────────────────────
 * Recent (flat)
 * ────────────────────────────────────────────────────────────────────── */

function buildRecentSection(
  allItems: InventoryItemResponse[],
  alreadyBoundIds: Set<number>,
): PickerSection[] {
  const eligible = allItems.filter(
    (it) => it.id != null && !alreadyBoundIds.has(it.id),
  );
  if (eligible.length === 0) return [];
  return [
    {
      key: 'recent',
      mode: 'recent',
      title: 'All items',
      subtitle: `${eligible.length} item${eligible.length === 1 ? '' : 's'}`,
      items: eligible,
    },
  ];
}

/* ──────────────────────────────────────────────────────────────────────
 * Scenes
 * ────────────────────────────────────────────────────────────────────── */

function buildSceneSections(
  scenes: FileWithInventoryResponse[],
  alreadyBoundIds: Set<number>,
): PickerSection[] {
  const out: PickerSection[] = [];
  for (const scene of scenes) {
    const eligible = (scene.inventory_items ?? []).filter(
      (it) => it.id != null && !alreadyBoundIds.has(it.id),
    );
    if (eligible.length === 0) continue;

    const datePart = formatShortDate(scene.created_at);
    const title =
      // Scenes-Phase-B AI label not shipped yet — fall back to filename.
      stripExtension(scene.filename) || `Snapshot #${scene.id}`;

    const subtitle = [
      datePart,
      `${eligible.length} item${eligible.length === 1 ? '' : 's'}`,
    ]
      .filter(Boolean)
      .join(' · ');

    out.push({
      key: `scene-${scene.id}`,
      mode: 'scenes',
      title,
      subtitle,
      heroPhotoUrl: scene.storage_url,
      fileId: scene.id,
      createdAt: scene.created_at,
      items: eligible,
    });
  }

  // Sort newest first.
  out.sort((a, b) => {
    const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return tb - ta;
  });

  return out;
}

/* ──────────────────────────────────────────────────────────────────────
 * Category
 * ────────────────────────────────────────────────────────────────────── */

function buildCategorySections(
  allItems: InventoryItemResponse[],
  alreadyBoundIds: Set<number>,
): PickerSection[] {
  const buckets = new Map<string, InventoryItemResponse[]>();
  for (const it of allItems) {
    if (it.id == null || alreadyBoundIds.has(it.id)) continue;
    const cat = (it.item_category ?? '').trim() || 'Uncategorized';
    if (!buckets.has(cat)) buckets.set(cat, []);
    buckets.get(cat)!.push(it);
  }

  const out: PickerSection[] = [];
  for (const [cat, items] of buckets.entries()) {
    if (items.length === 0) continue;
    out.push({
      key: `category-${cat}`,
      mode: 'category',
      title: cat,
      subtitle: `${items.length} item${items.length === 1 ? '' : 's'}`,
      items,
    });
  }

  // Alphabetical, with Uncategorized pinned to the end.
  out.sort((a, b) => {
    const aIsUncat = a.title.toLowerCase() === 'uncategorized';
    const bIsUncat = b.title.toLowerCase() === 'uncategorized';
    if (aIsUncat && !bIsUncat) return 1;
    if (!aIsUncat && bIsUncat) return -1;
    return a.title.localeCompare(b.title);
  });
  return out;
}

/* ──────────────────────────────────────────────────────────────────────
 * Quick-add chip computation
 *
 * Returns the data used to render the "Quick add" chip row at the top
 * of the picker. Empty array if no chips qualify (e.g. no scenes, or
 * every scene's items are already bound).
 * ────────────────────────────────────────────────────────────────────── */

export interface QuickAddChip {
  /** Stable key for the chip row. */
  key: string;
  /** Display label (e.g. "Latest scan"). */
  label: string;
  /** Item count shown on the chip ("· 14"). */
  count: number;
  /** Item ids the chip selects when tapped. Already-bound ids are
   *  pre-filtered out. */
  itemIds: number[];
}

export interface BuildQuickAddChipsArgs {
  allItems: InventoryItemResponse[];
  scenes: FileWithInventoryResponse[];
  alreadyBoundIds: Set<number>;
  /** Now in millis — overrideable for tests. */
  nowMs?: number;
}

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

export function buildQuickAddChips({
  allItems,
  scenes,
  alreadyBoundIds,
  nowMs = Date.now(),
}: BuildQuickAddChipsArgs): QuickAddChip[] {
  const chips: QuickAddChip[] = [];

  /* ── Latest scan ───────────────────────────────────────────────── */
  // scenes are not pre-sorted in the slice; sort defensively.
  const scenesSorted = [...scenes].sort((a, b) => {
    const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
    const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
    return tb - ta;
  });
  const latest = scenesSorted.find((s) => {
    const eligible = (s.inventory_items ?? []).filter(
      (it) => it.id != null && !alreadyBoundIds.has(it.id),
    );
    return eligible.length > 0;
  });
  if (latest) {
    const ids = (latest.inventory_items ?? [])
      .map((it) => it.id)
      .filter((id): id is number => id != null && !alreadyBoundIds.has(id));
    chips.push({
      key: 'latest-scan',
      label: 'Latest scan',
      count: ids.length,
      itemIds: ids,
    });
  }

  /* ── Last 7 days ───────────────────────────────────────────────── */
  const cutoff = nowMs - SEVEN_DAYS_MS;
  const last7Ids = new Set<number>();
  for (const scene of scenes) {
    if (!scene.created_at) continue;
    const t = new Date(scene.created_at).getTime();
    if (Number.isNaN(t) || t < cutoff) continue;
    for (const it of scene.inventory_items ?? []) {
      if (it.id != null && !alreadyBoundIds.has(it.id)) {
        last7Ids.add(it.id);
      }
    }
  }
  if (last7Ids.size > 0) {
    chips.push({
      key: 'last-7-days',
      label: 'Last 7 days',
      count: last7Ids.size,
      itemIds: Array.from(last7Ids),
    });
  }

  /* ── Most-frequent category ────────────────────────────────────── */
  const counts = new Map<string, number[]>();
  for (const it of allItems) {
    if (it.id == null || alreadyBoundIds.has(it.id)) continue;
    const cat = (it.item_category ?? '').trim();
    if (!cat) continue;
    if (!counts.has(cat)) counts.set(cat, []);
    counts.get(cat)!.push(it.id);
  }
  let bestCat: string | null = null;
  let bestIds: number[] = [];
  for (const [cat, ids] of counts.entries()) {
    if (ids.length > bestIds.length) {
      bestCat = cat;
      bestIds = ids;
    }
  }
  // Only surface the category chip when the most-frequent has ≥3 items
  // (avoids "All paint · 1" noise) and isn't already redundant with
  // Latest scan / Last 7 days.
  if (bestCat && bestIds.length >= 3) {
    chips.push({
      key: `category-${bestCat}`,
      label: `All ${bestCat}`,
      count: bestIds.length,
      itemIds: bestIds,
    });
  }

  return chips;
}
